/**
 * Часовой пояс филиала — простое СМЕЩЕНИЕ ОТ UTC, а не город.
 *
 * Раньше выбирали из списка городов (Алматы/Баку/Ташкент…), но сеть растёт и заранее
 * неизвестно, где откроется следующий филиал — держать актуальным справочник городов
 * невозможно. Поэтому человек выбирает «UTC+5:00», а не угадывает город.
 *
 * Источник истины — смещение в минутах (Branch.tz_offset_minutes): по нему считают время
 * календарь, часы в шапке и планировщик бота. Именованную зону (Branch.timezone) выводит
 * сервер из смещения — фронту она не нужна.
 */
export interface TzOption {
  /** Сдвиг от UTC в минутах (например, 300 = UTC+5). */
  offsetMin: number;
  /** Что видит человек: «UTC+5:00». */
  label: string;
}

/** Смещение по умолчанию для нового филиала: вся сеть, кроме Баку и Бишкека, живёт в UTC+5. */
export const DEFAULT_TZ_OFFSET_MIN = 5 * 60;

/** Границы допустимых смещений (те же, что проверяет сервер). */
export const MIN_TZ_OFFSET_MIN = -12 * 60;
export const MAX_TZ_OFFSET_MIN = 14 * 60;

/** Подпись смещения: «UTC+5:00», «UTC−3:00», «UTC+0:00». */
export function tzOffsetLabel(offsetMin: number | null | undefined): string {
  if (offsetMin == null || !Number.isFinite(offsetMin)) return 'Часовой пояс не задан';
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  // Знак — типографский минус, чтобы «UTC−3:00» читалось так же ровно, как «UTC+3:00».
  return `UTC${offsetMin < 0 ? '−' : '+'}${h}:${String(m).padStart(2, '0')}`;
}

/** Список для выпадающего меню: от UTC−12:00 до UTC+14:00, шагом в час. */
export const TZ_OFFSETS: TzOption[] = Array.from(
  { length: (MAX_TZ_OFFSET_MIN - MIN_TZ_OFFSET_MIN) / 60 + 1 },
  (_, i) => {
    const offsetMin = MIN_TZ_OFFSET_MIN + i * 60;
    return { offsetMin, label: tzOffsetLabel(offsetMin) };
  },
);

/**
 * Список смещений, гарантированно содержащий текущее значение филиала.
 * Нужен для карточки: если у филиала записано нецелое смещение (получасовой пояс),
 * оно не должно молча подмениться на соседнее при открытии карточки.
 */
export function tzOptionsWith(offsetMin: number | null | undefined): TzOption[] {
  if (offsetMin == null || TZ_OFFSETS.some((o) => o.offsetMin === offsetMin)) return TZ_OFFSETS;
  return [...TZ_OFFSETS, { offsetMin, label: tzOffsetLabel(offsetMin) }]
    .sort((a, b) => a.offsetMin - b.offsetMin);
}
